import DOMPurify from 'dompurify'

const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:'])

// Keep the allowed surface area intentionally small: announcements are rich-text, not a full HTML page.
const ANNOUNCEMENT_ALLOWED_TAGS = [
  'a',
  'b',
  'strong',
  'i',
  'em',
  'u',
  'p',
  'br',
  'ul',
  'ol',
  'li',
  'blockquote',
  'code',
  'pre',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
] as const

const ANNOUNCEMENT_ALLOWED_ATTR = ['href', 'target', 'rel', 'title'] as const

export function normalizeSafeUrl(rawUrl?: string | null): string | null {
  if (!rawUrl) return null
  const trimmed = rawUrl.trim()
  if (!trimmed) return null

  try {
    const url = new URL(trimmed, window.location.origin)
    if (!SAFE_PROTOCOLS.has(url.protocol)) return null
    return url.toString()
  } catch {
    return null
  }
}

export function openUrlInNewTabSafely(rawUrl?: string | null): boolean {
  const url = normalizeSafeUrl(rawUrl)
  if (!url) return false

  const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
  if (newWindow) newWindow.opener = null
  return true
}

let domPurifyHooksInstalled = false

function installDomPurifyHooksOnce() {
  if (domPurifyHooksInstalled) return
  domPurifyHooksInstalled = true

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (!(node instanceof Element)) return
    if (node.tagName !== 'A') return

    const href = node.getAttribute('href')
    if (href) {
      const safeHref = normalizeSafeUrl(href)
      if (!safeHref) node.removeAttribute('href')
      else node.setAttribute('href', safeHref)
    }

    // Prevent reverse-tabnabbing when announcements include links that open a new tab.
    const target = node.getAttribute('target')
    if (target === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer')
    }
  })
}

export function sanitizeAnnouncementHtml(dirtyHtml: string): string {
  installDomPurifyHooksOnce()

  return DOMPurify.sanitize(dirtyHtml, {
    USE_PROFILES: { html: true },
    ALLOW_DATA_ATTR: false,
    FORBID_ATTR: ['style'],
    ALLOWED_TAGS: [...ANNOUNCEMENT_ALLOWED_TAGS],
    ALLOWED_ATTR: [...ANNOUNCEMENT_ALLOWED_ATTR],
  })
}







